import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shell, Field, PasswordField } from "@/pages/Login";

export default function Signup() {
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const { applyAuth } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (form.password !== confirm) {
      setErrMsg("Passwords don't match. Please re-enter them.");
      toast.error("Passwords don't match. Please re-enter them.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, extra_seats: 0 });
      applyAuth(data);
      toast.success("Welcome! Your workspace is ready.");
      navigate("/library");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || (err.response ? "Sign up failed. Please try again." : "Can't reach the server. Check your connection and try again.");
      setErrMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Create your company" subtitle="You'll be the owner. Start free — subscribe anytime from the Team page.">
      <form onSubmit={submit}>
        <Field label="Company name" required value={form.company_name} onChange={set("company_name")} data-testid="signup-company" />
        <Field label="Your name" required value={form.name} onChange={set("name")} data-testid="signup-name" />
        <Field label="Work email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email" />
        <PasswordField label="Password" required minLength={6} value={form.password} onChange={set("password")} testid="signup-password" />
        <PasswordField label="Confirm password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="signup-confirm-password" />
        {confirm && form.password !== confirm && (
          <p data-testid="password-mismatch" className="-mt-2 mb-4 text-xs font-medium text-red-600">Passwords don't match.</p>
        )}
        {errMsg && (
          <p data-testid="signup-error" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{errMsg}</p>
        )}
        <button type="submit" disabled={loading} data-testid="signup-submit"
          className="w-full rounded-full bg-[var(--brand)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
          {loading ? "Creating…" : "Create my account"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-[var(--ink-soft)]">
        Already have an account? <Link to="/login" className="font-semibold text-[var(--brand)]">Log in</Link>
      </p>
    </Shell>
  );
}
